<template>
  <div class="fullscreen-page" @click="toggleCleanMode" :class="{ 'clean-mode': isCleanMode }">
    <!-- 背景装饰 -->
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <desktop-brand-corner />

    <button type="button" class="back-btn" @click.stop="handleGoBack" aria-label="返回上一页">
      <font-awesome-icon icon="chevron-left" />
    </button>

    <!-- 顶部：中文意思 -->
    <header class="page-header" :class="{ hidden: !showTranslation }">
      <h1 class="word-translation" :class="{ visible: true }">
        {{ currentCard.zh }}
      </h1>
    </header>

    <!-- 右侧功能按钮 -->
    <div class="side-controls" :class="{ hidden: isCleanMode }">
      <button
        class="control-btn favorite-btn"
        :class="{ active: isCurrentCardFavorite }"
        @click.stop="toggleFavorite"
        :title="isCurrentCardFavorite ? '取消收藏' : '收藏'"
      >
        <font-awesome-icon icon="heart" />
      </button>
      <button class="control-btn" @click.stop="openSettings" title="设置">
        <font-awesome-icon icon="cog" />
      </button>
      <button
        class="control-btn auto-play-btn"
        :class="{ active: isAutoMode }"
        @click.stop="startAutoPlay"
        :title="isAutoMode ? '自动播放中' : '开始自动播放'"
      >
        <font-awesome-icon :icon="isAutoMode ? 'book-open' : ['fas', 'hand-pointer']" />
      </button>
    </div>

    <!-- 中间内容区域 -->
    <div class="main-content">
      <!-- 视频展示 -->
      <div class="video-container" @click.stop="togglePlay">
        <div class="video-wrapper" :style="{ background: currentCard.bgColor }">
          <video
            ref="fullscreenVideoRef"
            class="fullscreen-video"
            :src="cardVideoUrl"
            playsinline
            preload="auto"
            controlslist="nodownload nofullscreen noremoteplayback"
            disablepictureinpicture
            @play="handleVideoPlay"
            @pause="handleVideoPause"
            @ended="handleVideoEnded"
          ></video>

          <div v-if="!isPlaying" class="video-status" aria-hidden="true">
            <font-awesome-icon icon="play" />
          </div>
        </div>
      </div>

      <!-- 单词展示 -->
      <h2 class="word-display" :class="{ visible: true }">
        {{ currentCard.en }}
      </h2>
    </div>

    <!-- 底部控制栏 -->
    <footer class="bottom-controls" :class="{ hidden: isCleanMode }">
      <div class="play-section">
        <button class="play-btn" @click.stop="togglePlay">
          <font-awesome-icon :icon="isPlaying ? 'pause' : 'play'" />
        </button>
      </div>
    </footer>

    <!-- 左下角进度 -->
    <div class="progress-indicator">
      <span class="progress-text">{{ currentIndex + 1 }}/{{ cards.length }}</span>
    </div>

    <!-- 左右切换按钮 -->
    <button class="nav-btn prev" @click.stop="prevCard">
      <font-awesome-icon icon="chevron-left" />
    </button>
    <button class="nav-btn next" @click.stop="nextCard">
      <font-awesome-icon icon="chevron-right" />
    </button>

    <!-- 触摸滑动区域 -->
    <div class="swipe-area" ref="swipeArea">
    </div>

    <!-- 设置弹窗 -->
    <div class="settings-modal" v-if="showSettings" @click.stop="closeSettings">
      <div class="settings-content" @click.stop>
        <div class="settings-list">
          <div class="settings-item">
            <div class="settings-text">
              <p class="settings-title">显示翻译</p>
              <p class="settings-desc">翻译显示在绘本顶部</p>
            </div>
            <label class="switch">
              <input type="checkbox" v-model="showTranslation">
              <span class="slider"></span>
            </label>
          </div>
          <div class="settings-item">
            <div class="settings-text">
              <p class="settings-title">手动翻页时音频自动播放</p>
            </div>
            <label class="switch">
              <input type="checkbox" v-model="manualFlipAutoPlay">
              <span class="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const cardVideoUrl = new URL('../assets/card.mp4', import.meta.url).href

// 数据
const cards = ref([
  { id: 1, en: 'Apple', zh: '苹果', icon: '🍎', color: '#FF6B6B', bgColor: 'rgba(255, 107, 107, 0.15)' },
  { id: 2, en: 'Ball', zh: '球', icon: '⚽', color: '#4ECDC4', bgColor: 'rgba(78, 205, 196, 0.15)' },
  { id: 3, en: 'Cat', zh: '猫', icon: '🐱', color: '#45B7D1', bgColor: 'rgba(69, 183, 209, 0.15)' },
  { id: 4, en: 'Dog', zh: '狗', icon: '🐕', color: '#96CEB4', bgColor: 'rgba(150, 206, 180, 0.15)' },
  { id: 5, en: 'Elephant', zh: '大象', icon: '🐘', color: '#9B89B3', bgColor: 'rgba(155, 137, 179, 0.15)' },
  { id: 6, en: 'Flower', zh: '花', icon: '🌸', color: '#FFB6C1', bgColor: 'rgba(255, 182, 193, 0.15)' },
  { id: 7, en: 'Grapes', zh: '葡萄', icon: '🍇', color: '#8E44AD', bgColor: 'rgba(142, 68, 173, 0.15)' },
  { id: 8, en: 'House', zh: '房子', icon: '🏠', color: '#F39C12', bgColor: 'rgba(243, 156, 18, 0.15)' },
  { id: 9, en: 'Ice cream', zh: '冰淇淋', icon: '🍦', color: '#E91E63', bgColor: 'rgba(233, 30, 99, 0.15)' },
  { id: 10, en: 'Jellyfish', zh: '水母', icon: '🪼', color: '#00BCD4', bgColor: 'rgba(0, 188, 212, 0.15)' },
])

const currentIndex = ref(0)
const isCleanMode = ref(false)
const isPlaying = ref(false)
const isAutoMode = ref(false)
const autoPlayTimer = ref(null)
const showSettings = ref(false)
const showTranslation = ref(true)
const manualFlipAutoPlay = ref(true)
const favoriteCardIds = ref([])
const fullscreenVideoRef = ref(null)

// 当前卡片
const currentCard = computed(() => cards.value[currentIndex.value])
const isCurrentCardFavorite = computed(() => favoriteCardIds.value.includes(currentCard.value.id))

const toggleFavorite = () => {
  const cardId = currentCard.value.id

  if (favoriteCardIds.value.includes(cardId)) {
    favoriteCardIds.value = favoriteCardIds.value.filter((id) => id !== cardId)
    return
  }

  favoriteCardIds.value = [...favoriteCardIds.value, cardId]
}

const playVideo = async (restart = true) => {
  const video = fullscreenVideoRef.value
  if (!video) return

  if (restart) {
    video.currentTime = 0
  }

  try {
    await video.play()
  } catch (error) {
    isPlaying.value = false
  }
}

// 切换清屏模式
const toggleCleanMode = () => {
  isCleanMode.value = !isCleanMode.value
}

// 切换播放/暂停
const togglePlay = () => {
  if (isPlaying.value) {
    stopPlay()
  } else {
    void startPlay()
  }
}

// 开始播放
const startPlay = async (restart = true) => {
  await nextTick()
  await playVideo(restart)
  
  if (isAutoMode.value && !autoPlayTimer.value) {
    autoPlayTimer.value = setInterval(() => {
      nextCard(false)
    }, 3000)
  }
}

// 停止播放
const stopPlay = () => {
  isPlaying.value = false
  const video = fullscreenVideoRef.value
  if (video) {
    video.pause()
    video.currentTime = 0
  }

  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value)
    autoPlayTimer.value = null
  }
}

// 开始自动播放
const startAutoPlay = () => {
  if (isAutoMode.value) {
    isAutoMode.value = false
    stopPlay()
    return
  }

  isAutoMode.value = true
  void startPlay()
}



// 打开设置
const openSettings = () => {
  showSettings.value = true
}

const closeSettings = () => {
  showSettings.value = false
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/card')
}

// 上一张
const prevCard = (isManual = true) => {
  stopPlay()
  currentIndex.value = (currentIndex.value - 1 + cards.value.length) % cards.value.length
  if (!isManual || manualFlipAutoPlay.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
  if (isAutoMode.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
}

// 下一张
const nextCard = (isManual = true) => {
  stopPlay()
  currentIndex.value = (currentIndex.value + 1) % cards.value.length
  if (!isManual || manualFlipAutoPlay.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
  if (isAutoMode.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
}

// 触摸滑动
let touchStartX = 0
let touchEndX = 0

const handleTouchStart = (e) => {
  touchStartX = e.changedTouches[0].screenX
}

const handleTouchEnd = (e) => {
  touchEndX = e.changedTouches[0].screenX
  handleSwipe()
}

const handleSwipe = () => {
  const swipeThreshold = 50
  const diff = touchStartX - touchEndX
  
  if (Math.abs(diff) > swipeThreshold) {
    if (diff > 0) {
      // 向左滑，下一张
      nextCard()
    } else {
      // 向右滑，上一张
      prevCard()
    }
  }
}

// 键盘事件
const handleKeydown = (e) => {
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    prevCard()
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    nextCard()
  } else if (e.key === ' ') {
    e.preventDefault()
    togglePlay()
  } else if (e.key === 'Escape') {
    router.push('/category')
  }
}

const handleVideoPlay = () => {
  isPlaying.value = true
}

const handleVideoPause = () => {
  if (!isAutoMode.value) {
    isPlaying.value = false
  }
}

const handleVideoEnded = () => {
  if (!isAutoMode.value) {
    isPlaying.value = false
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  document.addEventListener('touchstart', handleTouchStart, { passive: true })
  document.addEventListener('touchend', handleTouchEnd, { passive: true })
  
  // 默认开始播放
  setTimeout(() => {
    void startPlay()
  }, 500)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('touchend', handleTouchEnd)
  stopPlay()
})
</script>

<style scoped>
/* 基础样式 */
.fullscreen-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #FFF8F0 0%, #F9F7F2 50%, #F5F0E8 100%);
  position: relative;
  overflow: hidden;
  user-select: none;
}

/* 背景装饰 */
.bg-gradient {
  position: fixed;
  inset: 0;
  z-index: -3;
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.6;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #FFD89B, #FF8C42);
  top: -100px;
  right: -100px;
  animation: float 8s ease-in-out infinite;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #A8E6CF, #4ECDC4);
  bottom: -50px;
  left: -50px;
  animation: float 10s ease-in-out infinite reverse;
}

.orb-3 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #FFB6C1, #FF6B6B);
  top: 50%;
  left: 20%;
  animation: float 12s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(30px, -30px); }
}

.back-btn {
  position: fixed;
  top: 32px;
  left: 24px;
  z-index: 110;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.88);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    transform 0.25s ease,
    background 0.25s ease,
    box-shadow 0.25s ease,
    color 0.25s ease;
}

.back-btn :deep(svg) {
  color: inherit;
}

.back-btn:hover {
  transform: scale(1.06);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  color: #ff8a3d;
}

.back-btn:active {
  transform: scale(0.96);
}

/* 顶部标题 */
.page-header {
  position: fixed;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  text-align: center;
  transition: opacity 0.28s ease;
}

.page-header.hidden {
  opacity: 0;
  pointer-events: none;
}

.word-translation {
  font-size: 36px;
  font-weight: 700;
  color: var(--text, #8e939c);
  opacity: 0.9;
  transition: all 0.3s ease;
}

/* 右侧控制按钮 */
.side-controls {
  position: fixed;
  right: 30px;
  top: 35%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 100;
  transition: opacity 0.3s ease;
}

.side-controls.hidden {
  opacity: 0;
  pointer-events: none;
}

.control-btn {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: var(--text, #2D3436);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.control-btn :deep(svg) {
  color: inherit;
}

.control-btn:hover {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
  color: #ff8a3d;
}

.control-btn:active {
  transform: scale(0.95);
}

.favorite-btn.active {
  color: #ff8a3d;
  background: rgba(255, 246, 237, 0.95);
  border-color: rgba(255, 169, 97, 0.72);
  box-shadow: 0 8px 26px rgba(255, 138, 61, 0.22);
}

.favorite-btn.active:hover {
  color: #ff7534;
}

.auto-play-btn.active {
  color: #ff8a3d;
  background: rgba(255, 246, 237, 0.95);
  border-color: rgba(255, 169, 97, 0.72);
  box-shadow: 0 8px 26px rgba(255, 138, 61, 0.22);
}

.auto-play-btn.active:hover {
  color: #ff7534;
}

/* 主内容区 */
.main-content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  width: 100%;
  max-width: 600px;
  padding: 0 20px;
}

/* 视频容器 */
.video-container {
  width: min(360px, 70vw);
  aspect-ratio: 4 / 5;
  cursor: pointer;
}

.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
}

.video-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.15);
}

.fullscreen-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.5);
}

.fullscreen-video::-webkit-media-controls {
  display: none !important;
}

.fullscreen-video::-webkit-media-controls-enclosure {
  display: none !important;
}

.video-status {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 24px;
  pointer-events: none;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
}

/* 单词展示 */
.word-display {
  font-size: 72px;
  font-weight: 800;
  color: var(--text, #2D3436);
  letter-spacing: 4px;
  transition: all 0.3s ease;
  animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 底部控制栏 */
.bottom-controls {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  transition: opacity 0.3s ease;
}

.bottom-controls.hidden {
  opacity: 0;
  pointer-events: none;
}

.play-section {
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-btn {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF8C42, #FF6B35);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 40px rgba(255, 140, 66, 0.4);
}

.play-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 15px 50px rgba(255, 140, 66, 0.5);
}

.play-btn:active {
  transform: scale(0.95);
}

/* 进度指示 */
.progress-indicator {
  position: fixed;
  bottom: 50px;
  left: 30px;
  z-index: 100;
}

.progress-text {
  font-size: 24px;
  font-weight: 700;
  color: var(--text, #2D3436);
  opacity: 0.7;
}

/* 左右切换按钮 */
.nav-btn {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #8e939c;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.nav-btn :deep(svg) {
  color: inherit;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(-50%) scale(1.1);
  color: #ff8a3d;
}

.nav-btn:active {
  transform: translateY(-50%) scale(0.95);
}

.nav-btn.prev {
  left: 30px;
}

.nav-btn.next {
  right: 30px;
}

/* 清屏模式 */
.clean-mode .nav-btn,
.clean-mode .side-controls {
  opacity: 0;
  pointer-events: none;
}

.clean-mode .word-display {
  font-size: 120px;
}

.clean-mode .word-translation {
  font-size: 48px;
}

/* 触摸区域 */
.swipe-area {
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

/* 设置弹窗 */
.settings-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.38);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(6px);
  padding: 20px;
}

.settings-content {
  width: min(780px, 94vw);
  background: #ECECEC;
  border-radius: 18px;
  box-shadow: 0 24px 52px rgba(0, 0, 0, 0.22);
  padding: 16px 0;
}

.settings-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
}

.settings-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.settings-title {
  margin: 0;
  color: #1b1b1f;
  font-size: clamp(18px, 2.8vw, 28px);
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
}

.settings-desc {
  margin: 0;
  color: #6b6b73;
  font-size: clamp(12px, 1.4vw, 18px);
  line-height: 1.35;
  white-space: nowrap;
}

/* 开关样式 */
.switch {
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  width: 70px;
  height: 38px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #cfcfd2;
  border-radius: 999px;
  transition: all 0.25s ease;
}

.slider::before {
  content: "";
  position: absolute;
  width: 30px;
  height: 30px;
  left: 4px;
  top: 4px;
  border-radius: 50%;
  background: #f2f2f2;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: transform 0.25s ease;
}

.switch input:checked + .slider {
  background: #ff6f35;
}

.switch input:checked + .slider::before {
  transform: translateX(32px);
}

@media (hover: hover) and (pointer: fine) {
  .back-btn {
    display: none;
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .back-btn {
    top: 24px;
    left: 16px;
  }

  .page-header {
    top: 30px;
  }

  .word-translation {
    font-size: 28px;
  }

  .video-container {
    width: min(280px, 68vw);
  }

  .word-display {
    font-size: 48px;
    letter-spacing: 2px;
  }

  .nav-btn {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .nav-btn.prev {
    left: 15px;
  }

  .nav-btn.next {
    right: 15px;
  }

  .side-controls {
    right: 15px;
    gap: 12px;
  }

  .control-btn {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .bottom-controls {
    bottom: 30px;
  }

  .play-btn {
    width: 70px;
    height: 70px;
    font-size: 28px;
  }

  .progress-indicator {
    bottom: 40px;
    left: 20px;
  }

  .progress-text {
    font-size: 18px;
  }


  .settings-content {
    width: min(560px, 96vw);
    padding: 10px 0;
  }

  .settings-item {
    padding: 14px 16px;
  }

  .settings-title {
    font-size: clamp(16px, 3.8vw, 22px);
  }

  .settings-desc {
    font-size: clamp(11px, 2.4vw, 15px);
  }

  .switch {
    width: 64px;
    height: 34px;
  }

  .slider::before {
    width: 26px;
    height: 26px;
    left: 4px;
    top: 4px;
  }

  .switch input:checked + .slider::before {
    transform: translateX(30px);
  }
  .clean-mode .word-display {
    font-size: 60px;
  }

  .clean-mode .word-translation {
    font-size: 32px;
  }
}

@media (max-width: 480px) {
  .video-container {
    width: min(240px, 72vw);
  }

  .word-display {
    font-size: 36px;
  }

  .main-content {
    gap: 20px;
  }
  .settings-content {
    width: 96vw;
  }

  .settings-item {
    padding: 12px 14px;
  }

  .settings-title {
    font-size: 14px;
    line-height: 1.25;
  }

  .settings-desc {
    font-size: 11px;
  }

  .switch {
    width: 58px;
    height: 30px;
  }

  .slider::before {
    width: 22px;
    height: 22px;
    left: 4px;
    top: 4px;
  }

  .switch input:checked + .slider::before {
    transform: translateX(28px);
  }
}
</style>
